// src/components/ResultsList.js

import React, { useRef, useEffect } from 'react';
import { FaRedo, FaShareAlt, FaDownload } from 'react-icons/fa';
import MobileMockup    from './MobileMockup';
import useHtml2CanvasExport from '../hooks/useHtml2CanvasExport';
import './ResultsList.css';

function formatElapsed(ms) {
  if (!ms && ms !== 0) return '';
  const totalSec = Math.floor(ms / 1000);
  const hh = Math.floor(totalSec / 3600);
  const mm = Math.floor((totalSec % 3600) / 60);
  const ss = totalSec % 60;
  if (hh > 0) return `${hh}:${String(mm).padStart(2,'0')}:${String(ss).padStart(2,'0')}`;
  return `${mm}:${String(ss).padStart(2,'0')}`;
}

export default function ResultsList({ result, showAll, onToggle, summary = null }) {
  const listRef   = useRef(null);
  const mobileRef = useRef(null);
  const { exportRef, shareRef } = useHtml2CanvasExport();

   // hide Home.js’s instructions AND filter controls while this is up
  useEffect(() => {
  // grab the three elements we want to hide
  const instr     = document.querySelector('.instructions-panel');
  const sidebar   = document.querySelector('.sidebar');
  const filterBtn = document.querySelector('button.d-md-none.mb-3');

  // stash each element’s original inline state/value
  const originalInstrDisplay   = instr?.style.display;
  const origSidebarClasses     = sidebar?.className;
  const originalBtnDisplay     = filterBtn?.style.display;

  // hide them
  if (instr)     instr.style.display             = 'none';
  if (sidebar)   sidebar.classList.remove('d-md-block');
  if (filterBtn) filterBtn.style.display         = 'none';

  return () => {
    // restore on unmount
    if (instr)     instr.style.display           = originalInstrDisplay;
    if (sidebar)   sidebar.className             = origSidebarClasses;
    if (filterBtn) filterBtn.style.display       = originalBtnDisplay;
  };
  }, []);

  if (!result || !result.length) return null;

  const top    = result[0];
  const fave5  = result.slice(0, 5);
  const hated5 = result.slice(-5).reverse();

  // Helper to render your OL-based list
  const renderList = (list, start = 1) => (
    <ol start={start} className="results-list">
      {list.map((w, i) => (
        <li key={w.name} className="results-item">
          <img
            src={w.imageURL}
            // crossOrigin="anonymous"
            alt={w.name}
            className="result-thumb"
            onError={e => (e.currentTarget.src = 'https://static.wikia.nocookie.net/cjdm-wrestling/images/0/0a/Vacant_Superstar.png')}
          />
          <div className="result-info">
            <div className="result-name">{w.name}</div>
            <div className="result-company">({w.company})</div>
          </div>
        </li>
      ))}
    </ol>
  );

  // Decide which list block to show
  const listView = showAll || result.length <= 9
    ? (
      <div ref={listRef} className="results-container full-list">
        <h3>Full List</h3>
        {renderList(result)}
      </div>
    )
    : (
      <div ref={listRef} className="results-container">
        <div className="two-column">
          <div className="column">
            <h3>Fave 5</h3>
            {renderList(fave5)}
          </div>

          <div className="column">
            <h3>Hated 5</h3>
            {renderList(hated5, 1)}
          </div>
        </div>
      </div>
    );

  return (
    <>
      {listView}



      {/* off-screen mobile mockup for html2canvas */}
      <MobileMockup
        ref={mobileRef}
        top={top}
        fave5={fave5}
        hated5={hated5}
      />

      {/* Bottom-right: Restart, Share & Download stacked */}
      <footer className="app-footer">
        <div className="footer-left">  
        {result.length > 9 && (
          <button
            className="btn btn-link me-auto"
            onClick={onToggle}
            aria-label="Toggle View"
          >
            {showAll || result.length <= 9
              ? 'Show Top 5 & Bottom 5'
              : `Show All ${result.length}`}
          </button>
        )}
        </div>
      <div className="footer-center"> 
      {summary && (
        <div className="results-summary" role="status" aria-live="polite">
          <span className="full-text">
            <strong>You made {summary.choices} choices</strong> in <strong>{formatElapsed(summary.elapsedMs)}</strong>
          </span>
          <span className="compact-text" aria-hidden="true">
            <strong>{summary.choices}</strong> in <strong>{formatElapsed(summary.elapsedMs)}</strong>
          </span>
        </div>
      )}
      </div>
      <div className="footer-right">
      <button
        className="btn btn-outline-secondary"
        onClick={() => window.location.reload()}
        aria-label="Restart"
      >
        <FaRedo size={20} />
      </button>

      <button
        className="btn btn-outline-success"
        onClick={() =>
          shareRef(
            mobileRef,
            { backgroundColor: null, scale: 1 },
            'fave-hated.png',
            {
              title: 'My Favorite & Most Hated Five',
              text:  'Check out my Fave Five wrestling lineup!',
            }
          )
        }
        aria-label="Share"
      >
        <FaShareAlt size={20} />
      </button>

      <button
        className="btn btn-outline-primary"
        onClick={() =>
          exportRef(
            mobileRef,
            { backgroundColor: null, scale: 1 },
            'fave-hated.png'
          )
        }
        aria-label="Download"
      >
        <FaDownload size={20} />
      </button>
      </div>
      </footer>
    </>
  );
}
